import React, { useMemo } from 'react';
import { derivarPaleta, esHex, CLAVES_PALETA } from '../../utils/paleta';

// Campos de marca de una institución (nombre, textos, colores base y tema) con vista previa en
// vivo. Es un formulario controlado: recibe `valores` y avisa cambios por `onChange(nombre, valor)`.
// Lo usa "Mi institución" y, más adelante, el alta/edición de instituciones del SuperAdmin.

export const VALORES_BRANDING_VACIOS = {
  nombre: '',
  nombreCorto: '',
  descripcion: '',
  tituloPagina: '',
  colorPrimario: '',
  colorSecundario: '',
  colorAcento: '',
  tema: '',
};

// Convierte el DTO de la API en los valores del formulario.
export const valoresDesdeInstitucion = (inst) => ({
  nombre: inst.nombre || '',
  nombreCorto: inst.nombreCorto || '',
  descripcion: inst.descripcion || '',
  tituloPagina: inst.tituloPagina || '',
  colorPrimario: inst.colorPrimario || '',
  colorSecundario: inst.colorSecundario || '',
  colorAcento: inst.colorAcento || '',
  tema: inst.tema ? formatearTema(inst.tema) : '',
});

const formatearTema = (json) => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

// Paleta que resultará de los valores actuales (para la vista previa).
export const paletaDesdeValores = (valores) => {
  let tema = null;
  if (valores.tema && valores.tema.trim()) {
    try {
      tema = JSON.parse(valores.tema);
    } catch {
      tema = null;
    }
  }
  return derivarPaleta({
    primario: valores.colorPrimario,
    secundario: valores.colorSecundario,
    acento: valores.colorAcento,
    tema,
  });
};

// Valida el JSON del tema; devuelve un mensaje de error o null.
export const validarTema = (texto) => {
  if (!texto || !texto.trim()) return null;
  try {
    const obj = JSON.parse(texto);
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 'El tema debe ser un objeto JSON.';
    const desconocidas = Object.keys(obj).filter((k) => !CLAVES_PALETA.includes(k));
    if (desconocidas.length > 0) return `Claves desconocidas en el tema: ${desconocidas.join(', ')}.`;
    return null;
  } catch {
    return 'El tema no es un JSON válido.';
  }
};

const CampoColor = ({ etiqueta, nombre, valor, ayuda, onChange, colorRing }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{etiqueta}</label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={esHex(valor) ? valor : '#334155'}
        onChange={(e) => onChange(nombre, e.target.value)}
        className="w-11 h-10 p-0.5 border border-gray-300 rounded-lg cursor-pointer bg-white"
        title={etiqueta}
      />
      <input
        type="text"
        value={valor}
        onChange={(e) => onChange(nombre, e.target.value)}
        placeholder="#rrggbb"
        pattern="^#[0-9a-fA-F]{6}$"
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:border-transparent"
        style={{ '--tw-ring-color': colorRing }}
      />
    </div>
    {ayuda && <p className="text-xs text-gray-400 mt-1">{ayuda}</p>}
  </div>
);

const InstitucionBrandingForm = ({ valores, onChange, colorRing }) => {
  const paleta = useMemo(() => paletaDesdeValores(valores), [valores]);
  const errorTema = useMemo(() => validarTema(valores.tema), [valores.tema]);
  const inputClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent';
  const ring = { '--tw-ring-color': colorRing };
  const handle = (e) => onChange(e.target.name, e.target.value);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre *</label>
          <input type="text" name="nombre" value={valores.nombre} onChange={handle} required maxLength={200} className={inputClass} style={ring} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre corto *</label>
          <input type="text" name="nombreCorto" value={valores.nombreCorto} onChange={handle} required maxLength={100} className={inputClass} style={ring} />
          <p className="text-xs text-gray-400 mt-1">Se muestra en el panel y junto al logo.</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción</label>
        <textarea name="descripcion" value={valores.descripcion} onChange={handle} rows={2} maxLength={500} className={inputClass} style={ring} placeholder="Subtítulo del encabezado de la página principal" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Título de la pestaña del navegador</label>
        <input type="text" name="tituloPagina" value={valores.tituloPagina} onChange={handle} maxLength={200} className={inputClass} style={ring} placeholder="Publicaciones Virtuales — Mi institución" />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Colores</h3>
        <p className="text-xs text-gray-400 mb-4">
          De estos tres colores se deriva toda la paleta del sitio (encabezado, tarjetas, botones y enlaces).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <CampoColor etiqueta="Primario" nombre="colorPrimario" valor={valores.colorPrimario} onChange={onChange} colorRing={colorRing} ayuda="Inicio del degradado y color base." />
          <CampoColor etiqueta="Secundario" nombre="colorSecundario" valor={valores.colorSecundario} onChange={onChange} colorRing={colorRing} ayuda="Fin del degradado. Si se deja vacío, se oscurece el primario." />
          <CampoColor etiqueta="Acento" nombre="colorAcento" valor={valores.colorAcento} onChange={onChange} colorRing={colorRing} ayuda="Enlaces y llamadas a la acción. Vacío = primario." />
        </div>
      </div>

      {/* Vista previa */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Vista previa</h3>
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <div
            className="px-5 py-6"
            style={{ background: `linear-gradient(135deg, ${paleta.headerGradientStart}, ${paleta.headerGradientMid}, ${paleta.headerGradientEnd})` }}
          >
            <p className="text-white font-extrabold text-xl drop-shadow-md">Centro de Publicaciones</p>
            <p className="text-white/85 text-sm">{valores.descripcion || 'Descripción de la institución'}</p>
          </div>
          <div className="p-4 bg-gray-50 flex flex-wrap items-center gap-4">
            <div className="w-32 rounded-lg overflow-hidden border border-gray-200 bg-white">
              <div className="h-14" style={{ background: `linear-gradient(135deg, ${paleta.cardGradientFrom}, ${paleta.cardGradientVia}, ${paleta.cardGradientTo})` }} />
              <div className="px-2 py-1.5 text-xs font-semibold" style={{ color: paleta.ctaText }}>Explorar →</div>
            </div>
            <button
              type="button"
              className="px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md"
              style={{ background: `linear-gradient(135deg, ${paleta.accentFrom}, ${paleta.accentTo})` }}
            >
              Botón
            </button>
            <span className="text-sm font-medium" style={{ color: paleta.ctaText }}>Enlace de ejemplo</span>
          </div>
        </div>
      </div>

      <details className="group">
        <summary className="cursor-pointer text-sm font-semibold text-gray-900 select-none">
          Ajustes avanzados de paleta (JSON)
        </summary>
        <div className="mt-3">
          <textarea
            name="tema"
            value={valores.tema}
            onChange={handle}
            rows={8}
            spellCheck={false}
            className={`${inputClass} font-mono text-xs ${errorTema ? 'border-red-400' : ''}`}
            style={ring}
            placeholder={'{\n  "ctaText": "#dc2626",\n  "cardGradientTo": "#b91c1c"\n}'}
          />
          {errorTema ? (
            <p className="text-xs text-red-600 mt-1">{errorTema}</p>
          ) : (
            <p className="text-xs text-gray-400 mt-1">
              Sobrescribe claves puntuales de la paleta derivada. Claves disponibles: {CLAVES_PALETA.join(', ')}.
            </p>
          )}
        </div>
      </details>
    </div>
  );
};

export default InstitucionBrandingForm;
